import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Flag,
  AlertTriangle,
  Shield,
  Eye,
  MessageSquare,
  User,
  Zap,
  X,
  CheckCircle,
  Clock,
  Info,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReportReason {
  id: string;
  label: string;
  description: string;
  category: 'content' | 'behavior' | 'spam' | 'other';
  severity: 'low' | 'medium' | 'high';
  icon: React.ComponentType<{ className?: string }>;
}

interface ReportSystemProps {
  targetType: 'post' | 'comment' | 'user';
  targetId: string;
  targetTitle?: string;
  onReportSubmitted?: (reportId: string) => void;
}

const reportReasons: ReportReason[] = [
  {
    id: 'spam',
    label: '垃圾信息',
    description: '广告推广、刷屏、重复内容等',
    category: 'spam',
    severity: 'medium',
    icon: AlertTriangle
  },
  {
    id: 'inappropriate',
    label: '不当内容',
    description: '色情、暴力、血腥等不适宜内容',
    category: 'content',
    severity: 'high',
    icon: Eye
  },
  {
    id: 'harassment',
    label: '骚扰他人',
    description: '恶意攻击、人身攻击、网络霸凌',
    category: 'behavior',
    severity: 'high',
    icon: Shield
  },
  {
    id: 'false_info',
    label: '虚假信息',
    description: '传播谣言、虚假新闻等',
    category: 'content',
    severity: 'medium',
    icon: Info
  },
  {
    id: 'copyright',
    label: '侵犯版权',
    description: '未经授权使用他人作品',
    category: 'content',
    severity: 'medium',
    icon: Shield
  },
  {
    id: 'privacy',
    label: '侵犯隐私',
    description: '泄露他人隐私信息',
    category: 'content',
    severity: 'high',
    icon: Eye
  },
  {
    id: 'illegal',
    label: '违法内容',
    description: '涉及违法犯罪的内容',
    category: 'content',
    severity: 'high',
    icon: AlertTriangle
  },
  {
    id: 'off_topic',
    label: '内容不符',
    description: '与版块主题不符',
    category: 'content',
    severity: 'low',
    icon: Info
  },
  {
    id: 'other',
    label: '其他原因',
    description: '其他需要说明的问题',
    category: 'other',
    severity: 'low',
    icon: Flag
  }
];

export default function ReportSystem({ 
  targetType, 
  targetId, 
  targetTitle,
  onReportSubmitted 
}: ReportSystemProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'select' | 'details' | 'confirm'>('select');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [urgency, setUrgency] = useState<'normal' | 'urgent'>('normal');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setSubmitting(true);
    try {
      const reportData = {
        targetType,
        targetId,
        reason: selectedReason,
        customReason: selectedReason === 'other' ? customReason : '',
        evidence,
        isAnonymous,
        urgency,
        timestamp: new Date().toISOString()
      };

      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const reportId = `report_${Date.now()}`;
      onReportSubmitted?.(reportId);
      
      // 重置表单
      resetForm();
      
    } catch (error) {
      // 静默处理错误
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedReason('');
    setCustomReason('');
    setEvidence('');
    setIsAnonymous(true);
    setUrgency('normal');
    setStep('select');
    setOpen(false);
  };

  const selectedReasonData = reportReasons.find(r => r.id === selectedReason);

  const getTargetIcon = () => {
    switch (targetType) {
      case 'post': return <MessageSquare className="h-5 w-5" />;
      case 'comment': return <MessageSquare className="h-5 w-5" />;
      case 'user': return <User className="h-5 w-5" />;
      default: return <Flag className="h-5 w-5" />;
    }
  };

  const getTargetLabel = () => {
    switch (targetType) {
      case 'post': return '帖子';
      case 'comment': return '评论';
      case 'user': return '用户';
      default: return '内容';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'from-red-500 to-pink-500';
      case 'medium': return 'from-yellow-500 to-orange-500';
      case 'low': return 'from-blue-500 to-indigo-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-gray-500 hover:text-red-500 hover:bg-red-50 transition-all duration-200 group"
        >
          <Flag className="h-4 w-4 mr-1.5 group-hover:animate-pulse" />
          举报
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-md w-full mx-4 sm:mx-auto p-0 gap-0 overflow-hidden border-0 shadow-2xl">
        {/* 动态头部 */}
        <div className={cn(
          "relative text-white p-6 bg-gradient-to-br transition-all duration-500",
          step === 'select' ? getSeverityColor('medium') :
          step === 'details' ? getSeverityColor(selectedReasonData?.severity || 'medium') :
          'from-green-500 to-emerald-500'
        )}>
          <button
            onClick={resetForm}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              {step === 'select' && <Shield className="h-6 w-6" />}
              {step === 'details' && selectedReasonData && <selectedReasonData.icon className="h-6 w-6" />}
              {step === 'confirm' && <CheckCircle className="h-6 w-6" />}
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {step === 'select' && '选择举报类型'}
                {step === 'details' && '补充详细信息'}
                {step === 'confirm' && '确认提交'}
              </h2>
              <p className="text-white/80 text-sm">
                {step === 'select' && '维护社区环境，从我做起'}
                {step === 'details' && '提供更多信息有助于快速处理'}
                {step === 'confirm' && '请确认举报信息无误'}
              </p>
            </div>
          </div>

          {/* 进度指示器 */}
          <div className="flex items-center justify-center space-x-2 mt-6">
            {['select', 'details', 'confirm'].map((s, index) => (
              <div key={s} className="flex items-center">
                <div className={cn(
                  "w-3 h-3 rounded-full transition-all duration-300",
                  step === s ? "bg-white scale-110" : "bg-white/40"
                )} />
                {index < 2 && (
                  <div className={cn(
                    "w-8 h-0.5 mx-2 transition-all duration-300",
                    ['select', 'details', 'confirm'].indexOf(step) > index ? "bg-white" : "bg-white/40"
                  )} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* 步骤1: 选择举报原因 */}
          {step === 'select' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  {getTargetIcon()}
                  <span className="text-sm text-gray-500">举报{getTargetLabel()}</span>
                </div>
                {targetTitle && (
                  <p className="text-xs text-gray-400 truncate bg-gray-50 px-3 py-1 rounded-full">
                    {targetTitle}
                  </p>
                )}
              </div>

              <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
                <div className="space-y-3">
                  {reportReasons.map((reason) => {
                    const IconComponent = reason.icon;
                    return (
                      <div key={reason.id} className={cn(
                        "relative rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-lg group",
                        selectedReason === reason.id 
                          ? "border-red-300 bg-red-50 shadow-md" 
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      )}>
                        <RadioGroupItem 
                          value={reason.id} 
                          id={reason.id} 
                          className="absolute top-4 right-4"
                        />
                        <Label htmlFor={reason.id} className="cursor-pointer block pr-8">
                          <div className="flex items-start space-x-3">
                            <div className={cn(
                              "p-2.5 rounded-lg transition-colors",
                              selectedReason === reason.id 
                                ? "bg-red-100" 
                                : "bg-gray-100 group-hover:bg-gray-200"
                            )}>
                              <IconComponent className={cn(
                                "h-5 w-5 transition-colors",
                                selectedReason === reason.id 
                                  ? "text-red-600" 
                                  : "text-gray-600"
                              )} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-gray-900">{reason.label}</span>
                                <Badge 
                                  className={cn(
                                    "text-xs px-2 py-0.5",
                                    reason.severity === 'high' ? 'bg-red-100 text-red-700 border-red-200' :
                                    reason.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                    'bg-blue-100 text-blue-700 border-blue-200'
                                  )}
                                  variant="outline"
                                >
                                  {reason.severity === 'high' ? '严重' :
                                   reason.severity === 'medium' ? '中等' : '轻微'}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {reason.description}
                              </p>
                            </div>
                          </div>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>

              <div className="flex justify-end pt-6">
                <Button 
                  onClick={() => setStep('details')}
                  disabled={!selectedReason}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-8 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  下一步
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* 步骤2: 详细信息 */}
          {step === 'details' && (
            <div className="space-y-6">
              {/* 选中的举报原因回显 */}
              {selectedReasonData && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border-l-4 border-red-400">
                  <div className="flex items-center space-x-3 mb-2">
                    <selectedReasonData.icon className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-gray-900">{selectedReasonData.label}</span>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs",
                        selectedReasonData.severity === 'high' ? 'border-red-200 text-red-700' :
                        selectedReasonData.severity === 'medium' ? 'border-yellow-200 text-yellow-700' :
                        'border-blue-200 text-blue-700'
                      )}
                    >
                      {selectedReasonData.severity === 'high' ? '严重' :
                       selectedReasonData.severity === 'medium' ? '中等' : '轻微'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{selectedReasonData.description}</p>
                </div>
              )}

              {/* 自定义原因 */}
              {selectedReason === 'other' && (
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-gray-700 flex items-center">
                    详细说明
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    placeholder="请详细描述违规行为..."
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    rows={4}
                    className="resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 rounded-lg"
                  />
                </div>
              )}

              {/* 补充证据 */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">补充说明</Label>
                <Textarea
                  placeholder="如有相关证据或补充信息，请在此说明..."
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  rows={3}
                  className="resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 rounded-lg"
                />
              </div>

              {/* 举报选项 */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">匿名举报</p>
                      <p className="text-sm text-gray-600">保护您的身份信息</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={isAnonymous}
                    onCheckedChange={(checked) => setIsAnonymous(checked as boolean)}
                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-gray-700">处理优先级</Label>
                  <Select value={urgency} onValueChange={(value: 'normal' | 'urgent') => setUrgency(value)}>
                    <SelectTrigger className="focus:ring-2 focus:ring-red-500 focus:border-red-500 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span>普通处理</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="urgent">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span>紧急处理</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <Button 
                  variant="outline"
                  onClick={() => setStep('select')}
                  className="px-6 py-2.5 rounded-lg font-medium"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  上一步
                </Button>
                <Button 
                  onClick={() => setStep('confirm')}
                  disabled={selectedReason === 'other' && !customReason.trim()}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-8 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  确认提交
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* 步骤3: 确认提交 */}
          {step === 'confirm' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">确认举报信息</h3>
                <p className="text-sm text-gray-500">请确认以下信息无误后提交</p>
              </div>

              {/* 举报摘要 */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">举报类型</span>
                  <div className="flex items-center space-x-2">
                    {selectedReasonData && <selectedReasonData.icon className="h-4 w-4 text-gray-700" />}
                    <span className="font-semibold text-gray-900">{selectedReasonData?.label}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">举报对象</span>
                  <div className="flex items-center space-x-2">
                    {getTargetIcon()}
                    <span className="font-semibold text-gray-900">{getTargetLabel()}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">处理优先级</span>
                  <Badge variant={urgency === 'urgent' ? 'destructive' : 'secondary'} className="font-medium">
                    {urgency === 'urgent' ? '紧急' : '普通'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-600">匿名举报</span>
                  <span className="font-semibold text-gray-900">{isAnonymous ? '是' : '否'}</span>
                </div>
              </div>

              {/* 处理时间提示 */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Info className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">处理时间说明</p>
                    <p className="text-sm text-blue-700 leading-relaxed">
                      {selectedReasonData?.severity === 'high' ? '高优先级举报通常在24小时内处理完成' :
                       selectedReasonData?.severity === 'medium' ? '中等优先级举报在3个工作日内处理完成' :
                       '普通举报在7个工作日内处理完成'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <Button 
                  variant="outline"
                  onClick={() => setStep('details')}
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-lg font-medium"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  返回修改
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {submitting ? (
                    <>
                      <Zap className="mr-2 h-4 w-4 animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <Flag className="mr-2 h-4 w-4" />
                      确认提交
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t">
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            🛡️ 我们承诺认真对待每一个举报，恶意举报将承担相应责任
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}